import React from 'react';
import clsx from 'clsx';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { isSupported } from '@twilio/video-processors';

import BackgroundIcon from '../../../icons/BackgroundIcon';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';

const useStyles = makeStyles((theme: Theme) => ({
	active: {
		color: theme.palette.primary.main,
	},
	dot: {
		position: 'absolute',
		top: 8,
		right: 8,
		width: 9,
		height: 9,
		borderRadius: '50%',
		background: theme.palette.primary.main,
		boxShadow: '0 0 0 2px #fff',
	},
	spinner: {
		position: 'absolute',
		top: 6,
		right: 6,
	},
	srOnly: {
		position: 'absolute',
		width: 1,
		height: 1,
		padding: 0,
		margin: -1,
		overflow: 'hidden',
		clip: 'rect(0 0 0 0)',
		whiteSpace: 'nowrap',
		border: 0,
	},
}));

/**
 * Opens the in-call background panel. Rendered directly on the control bar so
 * blur / virtual backgrounds are never hidden behind a menu.
 */
export default function BackgroundButton(props: {
	className?: string;
	disabled?: boolean;
}) {
	const classes = useStyles();
	const {
		isBackgroundSelectionOpen,
		setIsBackgroundSelectionOpen,
		backgroundSettings,
		backgroundStatus,
	} = useVideoContext();
	const { setIsChatWindowOpen } = useChatContext();

	if (!isSupported) return null;

	const effectOn = backgroundSettings.type !== 'none';
	const preparing = backgroundStatus === 'loading';

	return (
		<Button
			className={clsx(props.className, {
				[classes.active]: isBackgroundSelectionOpen,
			})}
			onClick={() => {
				setIsBackgroundSelectionOpen(!isBackgroundSelectionOpen);
				setIsChatWindowOpen(false);
			}}
			disabled={props.disabled}
			startIcon={<BackgroundIcon />}
			aria-expanded={isBackgroundSelectionOpen}
			aria-controls="background-panel"
			data-cy-background-button
		>
			Background
			{preparing ? (
				<span className={classes.srOnly}> (preparing)</span>
			) : (
				effectOn && <span className={classes.srOnly}> (effect on)</span>
			)}
			{preparing ? (
				<CircularProgress
					size={12}
					className={classes.spinner}
					aria-hidden="true"
				/>
			) : (
				effectOn && <span className={classes.dot} aria-hidden="true" />
			)}
		</Button>
	);
}
